import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { trackPurchaseEdited } from '../services/analytics';
import { Toast, Field, PageHeader, LoadingSpinner } from './v2-utils';
import '../styles/v2.css';

const today = () => new Date().toISOString().split('T')[0];

export default function EditPurchaseV2() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [products, setProducts]           = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [unitTypes, setUnitTypes]         = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [unitSuppliers, setUnitSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [orderPrice, setOrderPrice]       = useState('');
  const [quantity, setQuantity]           = useState('');
  const [purchaseDate, setPurchaseDate]   = useState(today());
  const [dateWarning, setDateWarning]     = useState('');
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [toast, setToast]                 = useState(null);
  const [originalPurchase, setOriginalPurchase] = useState(null);

  useEffect(() => {
    Promise.all([
      axiosInstance.get('/products'),
      axiosInstance.get(`/purchases/${id}`),
    ]).then(async ([prodsRes, purchRes]) => {
      setProducts(prodsRes.data || []);
      const purch = purchRes.data;
      setOriginalPurchase(purch);
      setOrderPrice(String(purch.order_price || ''));
      setQuantity(String(purch.quantity || ''));
      setPurchaseDate(purch.purchase_date ? purch.purchase_date.split('T')[0] : today());

      const productId = purch.product_id;
      if (productId) {
        setSelectedProductId(String(productId));
        try {
          const unitsRes = await axiosInstance.get(`/units/product/${productId}`);
          const buying = (unitsRes.data || []).filter(u => u.unit_category === 'buying');
          setUnitTypes(buying);
          const matchUnit = buying.find(u => u.unit_id === purch.unit_id || u.unit_type === purch.unit_type);
          const resolvedUnit = matchUnit || (buying.length > 0 ? buying[0] : null);
          if (resolvedUnit) {
            setSelectedUnitId(String(resolvedUnit.unit_id));
            if (!matchUnit) setToast({ message: 'Original unit not found — please re-select.', severity: 'warning' });
            const suppliersRes = await axiosInstance.get(`/units/${resolvedUnit.unit_id}/suppliers`);
            setUnitSuppliers(suppliersRes.data || []);
            if (purch.supplier_id) setSelectedSupplierId(String(purch.supplier_id));
          }
        } catch {}
      }
    }).catch(() => setToast({ message: 'Failed to load purchase data.', severity: 'error' }))
    .finally(() => setLoading(false));
  }, [id]);

  const handleProductChange = async (productId) => {
    setSelectedProductId(productId);
    setSelectedUnitId('');
    setUnitTypes([]);
    setUnitSuppliers([]);
    setSelectedSupplierId('');
    if (!productId) return;
    try {
      const res = await axiosInstance.get(`/units/product/${productId}`);
      setUnitTypes((res.data || []).filter(u => u.unit_category === 'buying'));
    } catch {}
  };

  const handleUnitChange = async (unitId) => {
    setSelectedUnitId(unitId);
    setUnitSuppliers([]);
    setSelectedSupplierId('');
    if (!unitId) return;
    try {
      const res = await axiosInstance.get(`/units/${unitId}/suppliers`);
      setUnitSuppliers(res.data || []);
    } catch {}
  };

  const selectedUnit = unitTypes.find(u => String(u.unit_id) === String(selectedUnitId));
  const selectedProduct = products.find(p => String(p.product_id) === String(selectedProductId));
  const chosenSupplier = unitSuppliers.find(s => String(s.supplier_id) === String(selectedSupplierId));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProductId) { setToast({ message: 'Please select a product.', severity: 'error' }); return; }
    if (!selectedUnit) { setToast({ message: 'Please select a unit type.', severity: 'error' }); return; }
    if (unitSuppliers.length > 0 && !selectedSupplierId) { setToast({ message: 'Please select a supplier.', severity: 'error' }); return; }
    setSaving(true);
    try {
      await axiosInstance.put(`/purchases/${id}`, {
        product_name:  selectedProduct?.product_name || originalPurchase?.product_name,
        variety:       selectedProduct?.variety      || originalPurchase?.variety || '',
        supplier_name: chosenSupplier?.name || null,
        supplier_id:   chosenSupplier?.supplier_id || null,
        market_name:   null,
        order_price:   orderPrice,
        quantity,
        purchase_date: purchaseDate,
        unit_id: selectedUnitId,
      });
      trackPurchaseEdited(id);
      setToast({ message: 'Purchase updated successfully!', severity: 'success' });
      setTimeout(() => navigate('/new/dashboard/purchases/view'), 1000);
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update purchase.', severity: 'error' });
    } finally { setSaving(false); }
  };

  if (loading) return <LoadingSpinner message="Loading purchase…" />;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <PageHeader title="Edit Purchase" onBack={() => navigate('/new/dashboard/purchases/view')} />
      <form onSubmit={handleSubmit}>
        <div className="v2-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Purchase Details</p>

          {[
            { label: 'Product *', value: selectedProductId, onChange: e => handleProductChange(e.target.value), options: products.map(p => ({ value: String(p.product_id), label: `${p.product_name}${p.brand ? ` (${p.brand})` : ''}${p.variety ? ` - ${p.variety}` : ''}` })) },
            { label: 'Buying Unit *', value: selectedUnitId, onChange: e => handleUnitChange(e.target.value), options: unitTypes.map(u => ({ value: String(u.unit_id), label: `${u.unit_type} (${u.unit_category})` })), disabled: !unitTypes.length },
          ].map(({ label, value, onChange, options, disabled }) => (
            <Field key={label} label={label}>
              <div style={{ position: 'relative' }}>
                <select className="v2-input" style={{ paddingRight: 36, appearance: 'none', cursor: 'pointer' }}
                  value={value} onChange={onChange} required disabled={disabled}>
                  <option value="">Select…</option>
                  {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
              </div>
            </Field>
          ))}

          {selectedUnitId && (
            <Field label="Supplier">
              <div style={{ position: 'relative' }}>
                <select className="v2-input"
                  style={{ paddingRight: 36, appearance: 'none', cursor: unitSuppliers.length === 0 ? 'not-allowed' : 'pointer', opacity: unitSuppliers.length === 0 ? 0.5 : 1 }}
                  value={selectedSupplierId} onChange={e => setSelectedSupplierId(e.target.value)}
                  required={unitSuppliers.length > 0} disabled={unitSuppliers.length === 0}>
                  <option value="">{unitSuppliers.length === 0 ? 'No suppliers available' : 'Select supplier…'}</option>
                  {unitSuppliers.map(s => <option key={s.supplier_id} value={String(s.supplier_id)}>{s.name}</option>)}
                </select>
                <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
              </div>
            </Field>
          )}

          <div className="v2-sale-price-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label={selectedUnit ? `Order Price per ${selectedUnit.unit_type} (K) *` : 'Order Price (K) *'}>
              <input className="v2-input" type="number" value={orderPrice} onChange={e => setOrderPrice(e.target.value)} min="0" step="0.01" required />
            </Field>
            <Field label="Quantity *">
              <input className="v2-input" type="number" value={quantity} onChange={e => setQuantity(e.target.value)} min="0" step="0.01" required />
            </Field>
          </div>

          <Field label="Purchase Date *">
            <input className="v2-input" type="date" value={purchaseDate}
              onChange={e => { setPurchaseDate(e.target.value); setDateWarning(e.target.value > today() ? 'Note: Future date entered.' : ''); }} required />
            {dateWarning && <p style={{ fontSize: 11, color: '#f59e0b', margin: '4px 0 0', fontWeight: 500 }}>{dateWarning}</p>}
          </Field>

          <button type="submit" className="v2-btn-submit" disabled={saving} style={{ fontSize: 15 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
